import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Globe, LogOut, Shield, ChevronRight, Ticket, Plus, Package, History, CreditCard, Settings, Eye, EyeOff, ShoppingBag, Wallet, CheckCircle, ExternalLink, Gift, Loader2, MessageCircle, Coins, Zap, ArrowDownUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/Layout';
import { LoginRequired } from '../components/LoginRequired';
import { useAuth } from '../context/AuthContext';
import { useSolana } from '../context/WalletContext';
import { useToken } from '../context/TokenContext';
import { LevelCard } from '../components/LevelCard';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import i18n, { SUPPORTED_LANGUAGES } from '../i18n';
import 'flag-icons/css/flag-icons.min.css';
import axios from 'axios';
import { useSiteSettings } from '../context/SiteContext';
import { Skeleton } from '../components/ui/skeleton';
import countries from '../data/countries.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Profile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token, logout, isAdmin, refreshUser, isAuthenticated } = useAuth();
  const { publicKey, connected, balance: solBalance, tokenBalance, fetchBalance: fetchSolBalance, fetchTokenBalance, disconnect: disconnectWallet } = useSolana();
  const { tokenSettings, getTokenName, getTokenSymbol, getTokenIcon } = useToken();
  const { settings: siteSettings, loading: siteLoading } = useSiteSettings();
  const [language, setLanguage] = useState(i18n.language);
  
  // Solana settings
  const [solanaSettings, setSolanaSettings] = useState(null);
  
  // Profile edit states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [profileCountry, setProfileCountry] = useState(user?.country || '');
  const [savingProfile, setSavingProfile] = useState(false);

  // Voucher redeem states
  const [showVoucherDialog, setShowVoucherDialog] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [redeemingVoucher, setRedeemingVoucher] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSolanaSettings();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfileEmail(user.email || '');
      setProfileCountry(user.country || '');
    }
  }, [user]);

  // Refresh SOL balance when connected
  useEffect(() => {
    if (connected) {
      fetchSolBalance();
    }
  }, [connected]);

  // Fetch token balance when connected and token is active
  useEffect(() => {
    if (connected && tokenSettings?.is_active && tokenSettings?.contract_address) {
      fetchTokenBalance(tokenSettings.contract_address, tokenSettings.decimals || 9);
    }
  }, [connected, tokenSettings?.is_active, tokenSettings?.contract_address, tokenSettings?.decimals]);

  // Show login required if not authenticated
  if (!isAuthenticated) {
    return <LoginRequired feature={t('nav.profile', { defaultValue: 'Profile' })} />;
  }

  const fetchSolanaSettings = async () => {
    try {
      const res = await axios.get(`${API}/solana/settings`);
      setSolanaSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch Solana settings:', err);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          name: profileName || undefined,
          email: profileEmail || undefined,
          country: profileCountry || undefined
        }
      });
      
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success(t('profile.profileUpdated'));
      setShowProfileDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.updateFailed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLanguageChange = (value) => {
    setLanguage(value);
    i18n.changeLanguage(value);
    localStorage.setItem('language', value);
  };

  const handleLogout = async () => {
    // Disconnect wallet first if connected
    if (connected && disconnectWallet) {
      try {
        await disconnectWallet();
      } catch (e) {
        console.error('Failed to disconnect wallet:', e);
      }
    }
    logout();
    navigate('/');
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('profile.allFieldsRequired'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('profile.passwordMinLength'));
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/user/password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('profile.passwordChanged'));
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.passwordChangeFailed'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRedeemVoucher = async () => {
    if (!voucherCode.trim()) {
      toast.error(t('profile.enterVoucherCode'));
      return;
    }

    setRedeemingVoucher(true);
    try {
      const res = await axios.post(
        `${API}/vouchers/redeem`,
        { code: voucherCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setShowVoucherDialog(false);
      setVoucherCode('');
      // Refresh user data to update coin balance
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.voucherFailed'));
    } finally {
      setRedeemingVoucher(false);
    }
  };

  const menuItems = [
    {
      icon: Package,
      label: t('nav.inventory'),
      onClick: () => navigate('/inventory'),
      color: 'text-purple-500'
    },
    {
      icon: History,
      label: t('wallet.transactions'),
      onClick: () => navigate('/wallet'),
      color: 'text-blue-500'
    },
    {
      icon: MessageCircle,
      label: t('support.title'),
      onClick: () => navigate('/support'),
      color: 'text-amber-500'
    },
    ...(isAdmin ? [{
      icon: Shield,
      label: t('profile.admin'),
      onClick: () => navigate('/admin'),
      color: 'text-yellow-600'
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <SEOHead page="profile" />
      <PageHeader title={t('profile.title')} />

      <div className="px-5 py-6">
        {/* User Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl bg-white border border-[#F0E3C8] shadow-sm p-5 mb-6"
          data-testid="profile-user-info-card"
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-600 to-amber-900 flex items-center justify-center shadow-[0_0_30px_rgba(220,38,38,0.3)]">
              <User className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#1A1206] font-['Chakra_Petch']">{user?.name}</h2>
              {user?.wallet_address && (
                <p className="text-[#6E6452] text-xs font-mono">
                  {user.wallet_address.slice(0, 6)}...{user.wallet_address.slice(-4)}
                </p>
              )}
              {user?.email && (
                <p className="text-[#6E6452] text-sm">{user.email}</p>
              )}
              {user?.country && (
                <p className="text-[#6E6452] text-xs mt-0.5">{user.country}</p>
              )}
            </div>
            {/* Edit Profile Button */}
            <button
              onClick={() => setShowProfileDialog(true)}
              className="w-10 h-10 rounded-xl bg-[#FFF8EA] border border-[#F0E3C8] flex items-center justify-center hover:bg-[#FFF3D6] transition-colors"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5 text-[#6E6452]" />
            </button>
          </div>
        </motion.div>

        {/* Balance & Tickets Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-3 mb-6"
          data-testid="profile-balance-section"
        >
          {/* Coins Card */}
          <div 
            onClick={() => navigate('/wallet')}
            className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#FFF3D6] to-[#FFE9B0] border border-[#FFC300]/60 p-4 cursor-pointer hover:border-amber-500/50 transition-all active:scale-[0.98]"
          >
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-amber-500/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                    <Coins className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[#6E6452] text-xs uppercase tracking-wider">{t('wallet.balance')}</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                  <Plus className="w-4 h-4 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1A1206] font-['Chakra_Petch']" data-testid="user-coins">
                {user?.coins?.toFixed(0) || 0}
              </p>
              <p className="text-[#B26A00] text-xs">{t('wallet.tapToTopUp', { defaultValue: 'Tap to Top Up' })}</p>
            </div>
          </div>

          {/* Token Card (was Tickets) */}
          <div 
            onClick={() => navigate('/shop')}
            className="relative overflow-hidden rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 p-4 cursor-pointer hover:border-violet-500/50 transition-all active:scale-[0.98]"
          >
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-violet-500/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Ticket className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[#6E6452] text-xs uppercase tracking-wider">{t('wallet.tickets')}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-violet-600" />
              </div>
              <p className="text-2xl font-bold text-[#1A1206] font-['Chakra_Petch']" data-testid="user-tokens">
                {user?.tickets || 0}
              </p>
              <p className="text-violet-600 text-xs font-medium">{t('shop.tapToShop', { defaultValue: 'Tap to Shop' })}</p>
            </div>
          </div>

          {/* Swap Coin Card (below balance & tickets) */}
          <div 
            onClick={() => navigate('/swap')}
            className="col-span-2 relative overflow-hidden rounded-xl bg-gradient-to-r from-[#FFF3D6] to-[#FFF3D6] border border-[#FFC300]/60 p-3 cursor-pointer hover:border-amber-500/40 transition-all active:scale-[0.98]"
            data-testid="profile-swap-btn"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-600/20 flex items-center justify-center">
                  <ArrowDownUp className="w-4 h-4 text-[#C77800]" />
                </div>
                <div>
                  <p className="text-[#1A1206] text-sm font-bold">Swap Coins to SOL</p>
                  <p className="text-[#C98A2E] text-[10px]">Convert your coins to Solana</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#C98A2E]" />
            </div>
          </div>
        </motion.div>

        {/* Solana Wallet Section */}
        {solanaSettings?.is_active && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-6"
          >
            {/* Network Mode Banner */}
            {solanaSettings?.network === 'devnet' && (
              <div className="mb-3 bg-yellow-100 border border-yellow-300 rounded-xl p-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                <div>
                  <span className="text-yellow-700 text-xs font-semibold">
                    {t('wallet.devnetMode', { defaultValue: 'Devnet Mode (Testing)' })}
                  </span>
                  <p className="text-yellow-600 text-[10px]">
                    {t('wallet.devnetDesc', { defaultValue: 'Using test SOL, not real money' })}
                  </p>
                </div>
              </div>
            )}
            
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#FFF3D6] to-[#FFE9B0] border border-[#FFC300]/60 p-4">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="relative z-10" data-testid="profile-wallet-info">
                {connected ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                          <Wallet className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-[#1A1206] font-medium text-sm">{t('wallet.solanaWallet')}</span>
                        {/* Network Badge */}
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                          solanaSettings?.network === 'devnet' 
                            ? 'bg-yellow-600/20 text-yellow-700 border border-yellow-600/30' 
                            : 'bg-green-600/20 text-green-600 border border-green-600/30'
                        }`}>
                          {solanaSettings?.network === 'devnet' ? 'Devnet' : 'Mainnet'}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle className="w-3 h-3" /> {t('auth.walletConnected')}
                      </span>
                    </div>

                    {/* Wallet Address */}
                    <div className="flex items-center justify-between p-3 bg-white/80 border border-[#F0E3C8] rounded-lg">
                      <span className="text-[#6E6452] text-xs">{t('profile.walletAddress')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[#1A1206] text-xs font-mono">
                          {publicKey?.toBase58()?.slice(0, 6)}...{publicKey?.toBase58()?.slice(-6)}
                        </span>
                        <a
                          href={`https://solscan.io/account/${publicKey?.toBase58()}${solanaSettings?.network !== 'mainnet-beta' ? '?cluster=devnet' : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#C77800] hover:text-amber-300"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>

                    {/* SOL Balance */}
                    <div className="flex items-center justify-between p-3 bg-white/80 border border-[#F0E3C8] rounded-lg">
                      <span className="text-[#6E6452] text-xs">{t('wallet.solBalance')}</span>
                      <div className="text-right">
                        <p className="text-[#1A1206] text-lg font-bold font-['Chakra_Petch']">
                          {solBalance?.toFixed(4) || '0.0000'} <span className="text-[#C77800] text-sm">SOL</span>
                        </p>
                        <p className="text-[#6E6452] text-xs">
                          ≈ {((solBalance || 0) * (solanaSettings?.sol_to_coin_rate || 1000)).toFixed(0)} {t('wallet.coins')}
                        </p>
                      </div>
                    </div>

                    {/* Token Balance - Only show if token is active */}
                    {tokenSettings?.is_active && tokenSettings?.contract_address && (
                      <div className="flex items-center justify-between p-3 bg-white/80 border border-[#F0E3C8] rounded-lg">
                        <span className="text-[#6E6452] text-xs">{t('profile.tokenBalance')}</span>
                        <div className="text-right">
                          <p className="text-[#1A1206] text-lg font-bold font-['Chakra_Petch']">
                            {tokenBalance !== null ? tokenBalance.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '...'} <span className="text-purple-600 text-sm">{getTokenSymbol()}</span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-[#1A1206] font-medium text-sm">{t('wallet.solanaWallet')}</p>
                        <p className="text-[#6E6452] text-xs">{t('wallet.topUpWithSOL')}</p>
                      </div>
                    </div>
                    <WalletMultiButton className="!bg-violet-600 hover:!bg-violet-700 !rounded-xl !h-10 !text-xs !font-bold !px-4" />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Redeem Voucher Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="mb-6"
        >
          <button
            onClick={() => setShowVoucherDialog(true)}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl hover:border-green-600/50 transition-all active:scale-[0.99]"
            data-testid="redeem-voucher-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-[#1A1206] font-medium">{t('profile.redeemVoucher')}</p>
                <p className="text-[#6E6452] text-xs">{t('profile.enterCode')}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-green-500" />
          </button>
        </motion.div>

        {/* Language Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-6"
        >
          <label className="text-sm text-[#6E6452] uppercase tracking-wider mb-2 block">
            {t('profile.language')}
          </label>
          <Select value={language} onValueChange={handleLanguageChange}>
            <SelectTrigger 
              className="h-14 bg-white border-[#F0E3C8] text-[#1A1206] rounded-xl"
              data-testid="language-selector"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-[#6E6452]" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-white border-[#F0E3C8]">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code} className="text-[#1A1206] hover:bg-[#FFF3D6]">
                  <span className="flex items-center gap-2">
                    <span className={`fi fi-${lang.countryCode}`} />
                    {lang.nativeName}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Menu Items */}
        <div className="space-y-3 mb-8">
          {menuItems.map((item, index) => (
            <motion.button
              key={item.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              onClick={item.onClick}
              className="w-full flex items-center justify-between p-4 bg-white border border-[#F0E3C8] rounded-xl hover:bg-[#FFF3D6] transition-colors"
              data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                <item.icon className={`w-5 h-5 ${item.color || 'text-[#6E6452]'}`} />
                <span className="text-[#1A1206] font-medium">{item.label}</span>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6E6452]" />
            </motion.button>
          ))}
        </div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full h-14 border-red-500/50 text-red-500 hover:bg-red-900/20 hover:border-red-500 font-bold uppercase tracking-wider rounded-xl"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            {t('auth.logout')}
          </Button>
        </motion.div>

        {/* App Version */}
        <div className="mt-12 text-center pb-8">
          {siteLoading ? (
            <Skeleton className="h-4 w-32 mx-auto" />
          ) : (
            <p className="text-[#6E6452] text-sm">{siteSettings?.app_name || 'App'} v1.0.0</p>
          )}
        </div>
      </div>

      {/* Profile Edit Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="bg-white border-[#F0E3C8] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1A1206] text-xl font-['Chakra_Petch'] uppercase tracking-wider">
              {t('profile.editProfile')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Display Name */}
            <div className="space-y-2">
              <label className="text-sm text-[#6E6452]">{t('profile.displayName')}</label>
              <Input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder={t('profile.enterName', { defaultValue: 'Enter your name' })}
                className="h-12 bg-[#FFF8EA] border-[#F0E3C8] text-[#1A1206]"
                data-testid="profile-name"
              />
            </div>

            {/* Email for Notifications */}
            <div className="space-y-2">
              <label className="text-sm text-[#6E6452]">{t('profile.emailNotifications', { defaultValue: 'Email (for notifications)' })}</label>
              <Input
                type="email"
                value={profileEmail}
                onChange={(e) => setProfileEmail(e.target.value)}
                placeholder={t('profile.emailPlaceholder', { defaultValue: 'your@email.com' })}
                className="h-12 bg-[#FFF8EA] border-[#F0E3C8] text-[#1A1206]"
                data-testid="profile-email"
              />
              <p className="text-[#6E6452] text-xs">{t('profile.emailOptional', { defaultValue: 'Optional - used for transaction notifications only' })}</p>
            </div>

            {/* Country */}
            <div className="space-y-2">
              <label className="text-sm text-[#6E6452]">{t('inventory.country')}</label>
              <Select 
                value={profileCountry} 
                onValueChange={setProfileCountry}
              >
                <SelectTrigger 
                  className="h-12 bg-[#FFF8EA] border-[#F0E3C8] text-[#1A1206]"
                  data-testid="profile-country"
                >
                  <SelectValue placeholder={t('profile.selectCountry', { defaultValue: 'Select your country' })} />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F0E3C8] max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem 
                      key={country.code} 
                      value={country.name}
                      className="text-[#1A1206] hover:bg-[#FFF3D6]"
                    >
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Wallet Address (Read-only) */}
            {user?.wallet_address && (
              <div className="space-y-2">
                <label className="text-sm text-[#6E6452]">{t('profile.walletAddress')}</label>
                <div className="p-3 bg-[#FFF8EA] rounded-lg">
                  <p className="text-[#6E6452] text-sm font-mono break-all">{user.wallet_address}</p>
                </div>
              </div>
            )}

            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider rounded-xl"
              data-testid="save-profile-btn"
            >
              {savingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('common.loading')}
                </>
              ) : (
                t('profile.saveChanges')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voucher Redeem Dialog */}
      <Dialog open={showVoucherDialog} onOpenChange={setShowVoucherDialog}>
        <DialogContent className="bg-white border border-[#F0E3C8] max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-[#1A1206] text-xl font-['Chakra_Petch'] uppercase tracking-wider">
              {t('profile.redeemVoucher', { defaultValue: 'Redeem Voucher' })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Balance */}
            <div className="p-4 bg-gradient-to-r from-[#FFF3D6] to-[#FFF3D6] border border-[#FFC300]/60 rounded-xl">
              <p className="text-[#6E6452] text-xs mb-1">{t('profile.currentBalance', { defaultValue: 'Current Balance' })}</p>
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-[#C77800]" />
                <span className="text-2xl font-bold text-[#1A1206] font-['Chakra_Petch']">{user?.coins?.toFixed(0) || 0}</span>
                <span className="text-[#C77800] text-sm">{t('wallet.coins')}</span>
              </div>
            </div>

            {/* Voucher Code Input */}
            <div className="space-y-2">
              <label className="text-sm text-[#6E6452]">{t('profile.voucherCode', { defaultValue: 'Voucher Code' })}</label>
              <Input
                type="text"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                placeholder={t('profile.enterVoucherCode', { defaultValue: 'Enter voucher code' })}
                className="h-12 bg-[#FFF8EA] border-[#F0E3C8] text-[#1A1206] uppercase font-mono tracking-wider text-center text-lg"
                data-testid="voucher-code-input"
              />
            </div>

            {/* Info */}
            <div className="p-3 bg-[#FFF8EA] rounded-lg">
              <p className="text-[#6E6452] text-xs text-center">
                {t('profile.voucherInfo', { defaultValue: 'Enter the voucher code you received. Coins will be added to your balance immediately.' })}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowVoucherDialog(false);
                  setVoucherCode('');
                }}
                variant="outline"
                className="flex-1 h-12 border-[#F0E3C8] text-[#6E6452] hover:text-[#1A1206]"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleRedeemVoucher}
                disabled={redeemingVoucher || !voucherCode.trim()}
                className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider rounded-xl"
                data-testid="submit-voucher-btn"
              >
                {redeemingVoucher ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('messages.processing')}
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    {t('shop.redeem')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
